import { UserSort } from '@simppl/repository-admin/common';

/**
 * 권한 키 (Permission Key)
 * 각 기능/페이지에 대한 권한을 식별하는 키
 */
export const PermissionKey = {
  // 회원 관리
  USER_MANAGEMENT: 'USER_MANAGEMENT',

  // 커뮤니티 관리
  COMMUNITY_MANAGEMENT: 'COMMUNITY_MANAGEMENT',

  // 입시 관리
  ADMISSION_MANAGEMENT: 'ADMISSION_MANAGEMENT',

  // 포인트 관리
  POINT_MANAGEMENT: 'POINT_MANAGEMENT',

  // 모의 지원자 관리
  MOCK_APPLICANT_MANAGEMENT: 'MOCK_APPLICANT_MANAGEMENT',

  // 모의지원 관리
  SIMULATED_APPLICATION_MANAGEMENT: 'SIMULATED_APPLICATION_MANAGEMENT',

  // 통계
  STATISTICS: 'STATISTICS',
  STATISTICS_LOGS: 'STATISTICS_LOGS',

  // 제재 관리
  SANCTION_MANAGEMENT: 'SANCTION_MANAGEMENT',

  // 데이터베이스 관리
  DATABASE_MANAGEMENT: 'DATABASE_MANAGEMENT',

  // 시스템 관리
  SYSTEM_MANAGEMENT: 'SYSTEM_MANAGEMENT',

  // 문제 관리
  PROBLEM_SOLVED_MANAGEMENT: 'PROBLEM_SOLVED_MANAGEMENT',
} as const;

export type PermissionKey = (typeof PermissionKey)[keyof typeof PermissionKey];

/**
 * 권한 정책: Role(UserSort) -> PermissionKey[]
 * 한 곳에서 모든 권한을 관리
 */
export const ROLE_PERMISSIONS: Record<UserSort, PermissionKey[]> = {
  [UserSort.USER]: [],
  [UserSort.ADMIN]: [
    PermissionKey.USER_MANAGEMENT,
    PermissionKey.COMMUNITY_MANAGEMENT,
    PermissionKey.ADMISSION_MANAGEMENT,
    PermissionKey.POINT_MANAGEMENT,
    PermissionKey.MOCK_APPLICANT_MANAGEMENT,
    PermissionKey.SIMULATED_APPLICATION_MANAGEMENT,
    PermissionKey.STATISTICS,
    PermissionKey.STATISTICS_LOGS,
    PermissionKey.SANCTION_MANAGEMENT,
    PermissionKey.DATABASE_MANAGEMENT,
    PermissionKey.SYSTEM_MANAGEMENT,
    PermissionKey.PROBLEM_SOLVED_MANAGEMENT,
  ],
  [UserSort.COMMUNITY_ADMIN]: [PermissionKey.COMMUNITY_MANAGEMENT, PermissionKey.SANCTION_MANAGEMENT],
  [UserSort.VERITAS_ADMIN]: [PermissionKey.STATISTICS],
};


/**
 * 사용자의 권한 목록 조회
 */
export function getRolePermissions(userSort: UserSort | undefined): PermissionKey[] {
  if (!userSort) return [];
  return ROLE_PERMISSIONS[userSort] ?? [];
}

/**
 * 사용자가 특정 권한을 가지고 있는지 확인
 */
export function hasPermission(userSort: UserSort | undefined, permission: PermissionKey): boolean {
  if (!userSort) return false;

  // ADMIN은 모든 권한 보유
  if (userSort === UserSort.ADMIN) return true;

  const permissions = ROLE_PERMISSIONS[userSort];
  return permissions.includes(permission);
}

/**
 * 사용자가 여러 권한 중 하나라도 가지고 있는지 확인 (OR 조건)
 */
export function hasAnyPermission(
  userSort: UserSort | undefined,
  permissions: PermissionKey[],
): boolean {
  if (!userSort || permissions.length === 0) return false;

  return permissions.some((permission) => hasPermission(userSort, permission));
}

/**
 * 사용자가 모든 권한을 가지고 있는지 확인 (AND 조건)
 */
export function hasAllPermissions(
  userSort: UserSort | undefined,
  permissions: PermissionKey[],
): boolean {
  if (!userSort || permissions.length === 0) return false;

  return permissions.every((permission) => hasPermission(userSort, permission));
}

/**
 * 사용자가 필요한 권한들을 가지고 있는지 확인
 * (기본: OR 조건, requireAll=true 시 AND 조건)
 */
export function canAccessByPermissions(
  userSort: UserSort | undefined,
  requiredPermissions: PermissionKey[],
  requireAll = false,
): boolean {
  if (!userSort) return false;
  if (requiredPermissions.length === 0) return true; // 권한 요구사항 없으면 허용

  // ADMIN은 모든 권한 보유
  if (userSort === UserSort.ADMIN) return true;

  return requireAll
    ? hasAllPermissions(userSort, requiredPermissions)
    : hasAnyPermission(userSort, requiredPermissions);
}
